import { useState, useCallback } from "react";
import useScanner from "../../hooks/useScanner";
import ScanModal from "../ScanModal/ScanModal";
import LotSelectorModal from "../LotSelectorModal/LotSelectorModal";
import ProductSearchModal from "../ProductSearchModal/ProductSearchModal";
import { generateTxId } from "../../utils/formatters";
import { parseGS1 } from "../../utils/gs1Parser";
import styles from "./Workflow.module.css";

export default function RemoveWorkflow({ products, user, onTransaction, onBack }) {
  const [foundProduct, setFoundProduct] = useState(null);
  const [showQuantity, setShowQuantity] = useState(false);
  const [showLotSelector, setShowLotSelector] = useState(false);
  const [lotMatches, setLotMatches] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [lastTx, setLastTx] = useState(null);
  const [error, setError] = useState("");

  const handleScan = useCallback((rawBarcode) => {
    setError("");
    setLastTx(null);
    const gs1 = parseGS1(rawBarcode);
    const lookupBarcode = gs1.isGS1 ? gs1.gtin : rawBarcode;
    const matches = products.filter(p => (p.barcode || p.id) === lookupBarcode);
    if (matches.length === 1) {
      setFoundProduct(matches[0]);
      setShowQuantity(true);
    } else if (matches.length > 1) {
      setLotMatches(matches.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setShowLotSelector(true);
    } else {
      setError(`Item "${lookupBarcode}" not found in inventory.`);
    }
  }, [products]);

  const { error: scanError } = useScanner({
    onScan: handleScan,
    enabled: !showQuantity && !showLotSelector && !showSearch,
  });

  const handleSearchSelect = (lots) => {
    setShowSearch(false);
    setError("");
    setLastTx(null);
    if (lots.length === 1) {
      setFoundProduct(lots[0]);
      setShowQuantity(true);
    } else if (lots.length > 1) {
      setLotMatches(lots.sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")));
      setShowLotSelector(true);
    }
  };

  const handleLotSelect = (product) => {
    setShowLotSelector(false);
    setFoundProduct(product);
    setShowQuantity(true);
  };

  const handleConfirmRemove = async ({ qty, notes }) => {
    const newStock = foundProduct.stock - qty;
    const tx = {
      id: generateTxId(),
      productId: foundProduct.id,
      productName: foundProduct.description,
      manufacturer: foundProduct.manufacturer,
      type: "OUT",
      quantity: qty,
      notes,
      performedBy: user.name,
      timestamp: new Date().toISOString(),
      stockAfter: newStock,
    };
    onTransaction(tx, foundProduct.id, newStock);
    setLastTx(tx);
    setShowQuantity(false);
    setFoundProduct(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button onClick={onBack} className={styles.backBtn}>← Back</button>
        <div className={styles.workflowTitle}>Remove from Inventory</div>
      </div>

      {lastTx && (
        <div className={styles.success}>
          <div className={styles.successLabel}>Removed Successfully</div>
          <div className={styles.successName}>{lastTx.productName}</div>
          <div className={styles.successMeta}>-{lastTx.quantity} — Remaining: {lastTx.stockAfter}</div>
          <button onClick={() => setLastTx(null)} className={styles.dismissBtn}>dismiss</button>
        </div>
      )}

      {(error || scanError) && (
        <div className={styles.error}>! {error || scanError}</div>
      )}

      <div className={styles.scannerCard}>
        <div className={styles.readyState}>
          <div className={styles.readyIcon}>−</div>
          <div className={styles.readyTitle}>SCAN ITEM TO REMOVE</div>
          <div className={styles.readyHint}>Point barcode scanner at item</div>
          <div className={styles.readyDivider}>or</div>
          <button onClick={() => setShowSearch(true)} className={styles.manualSearchBtn}>
            Search Manually
          </button>
        </div>
      </div>

      {showLotSelector && (
        <LotSelectorModal lots={lotMatches} mode="remove" onSelect={handleLotSelect}
          onClose={() => { setShowLotSelector(false); setLotMatches([]); }} />
      )}
      {showQuantity && foundProduct && (
        <ScanModal product={foundProduct} mode="remove" onConfirm={handleConfirmRemove}
          onClose={() => { setShowQuantity(false); setFoundProduct(null); }} />
      )}
      {showSearch && (
        <ProductSearchModal products={products} mode="remove" onSelect={handleSearchSelect}
          onClose={() => setShowSearch(false)} />
      )}
    </div>
  );
}
